import type { OrderSubmissionCheckState } from '../models/types/order-preferences.types';

/**
 * PD-16276 — the vocabulary every order-submission check shares.
 *
 * Each check is configured per channel (CRM or storefront) at one of the
 * OrderSubmissionCheckState levels. The rules in this folder receive the
 * level already resolved for the caller and return findings that carry the
 * level that fired. Resolving the level stays with the caller because the
 * CRM cart and the server read the preference differently (the cart hides
 * 'hold', which is server-only, and keeps a few legacy permission
 * fallbacks). The decision itself lives here, once, so the cart can never
 * show something the server would not enforce, or the reverse.
 */
export type OrderCheckLevel = OrderSubmissionCheckState;

/** A level that produces a finding, which is every level except 'off'. */
export type OrderCheckFiringLevel = Exclude<OrderCheckLevel, 'off'>;

/** How the server acts on a finding. */
export type OrderCheckSeverity = 'error' | 'hold' | 'warning';

export function isOrderCheckOn(
	level: OrderCheckLevel | null | undefined,
): level is OrderCheckFiringLevel {
	return level != null && level !== 'off';
}

/**
 * 'error' blocks the submission, 'hold' and 'holdWarning' place the order in
 * Approval Required, and 'warning' only informs.
 */
export function orderCheckSeverity(
	level: OrderCheckFiringLevel,
): OrderCheckSeverity {
	if (level === 'error') {
		return 'error';
	}

	if (level === 'hold' || level === 'holdWarning') {
		return 'hold';
	}

	return 'warning';
}

/**
 * A yes/no column as it actually arrives. The flag columns these checks read
 * (ship-to schedule approvals, non-inventory, full-pallet-only, short-dated)
 * are MySQL tinyints, which TypeORM hands back as 1/0 rather than true/false,
 * while the CRM cart receives real booleans. A strict `=== true` test fails
 * every server-side 1, so each check reads its flags through this.
 */
export type OrderCheckFlag = boolean | number | null | undefined;

export function isFlagOn(value: OrderCheckFlag): boolean {
	return value === true || value === 1;
}

/**
 * A line takes part in the line checks only when something is being ordered
 * on it. The CRM cart has always skipped empty lines, and the server follows
 * the same rule so a zero-quantity line can never hold or block an order.
 */
export function isOrderedQuantity(
	quantity: number | null | undefined,
): quantity is number {
	return quantity != null && Number.isFinite(quantity) && quantity > 0;
}
